/**
 * Phase 8 T-06 §7 D / E → T-06b §20.B surface A · Smart Match agent column.
 *
 * Thin adapter that renders the unified `<AgentThread scope="smart-match">`
 * with a llama-backed `runPrompt` so we keep T-06's real model integration
 * while consolidating the UI under one component tree (§20.E — no duplicate
 * copies of `<AgentThread>`).
 *
 * The imperative handle (`sendPrompt`) is preserved for
 * `CandidateContextRail`'s "Ask about this candidate…" input — it now
 * proxies through `<AgentThread>` via a composer focus + synthetic send
 * pathway kept minimal (we call the llama runner directly then route the
 * resulting turn into the shared thread).
 */

import { useCallback, useEffect, useRef } from 'react';
import { useAuth } from '@core/auth/useAuth';
import { SourcingConnectorStatus } from '@modules/sourcing/components/SourcingConnectorStatus';
import { invokeLlamaChat, type LlamaChatMessage } from '@core/ai/llamaChat';
import {
  AGENT_SYSTEM_PROMPT,
  TOOL_DEFINITIONS,
  OPENAI_TOOL_DEFINITIONS,
  parseOpenAIToolCalls,
  parseToolCall,
  executeTool,
  toolResultSummary,
  augmentAssistantHistoryForLlm,
  type ToolCall,
} from '@core/ai/agentTools';
import { AgentThread } from '@modules/agent/components/AgentThread';
import type { AgentReasoningStep } from '@modules/agent/hooks/useAgentThread';
import type { IntakeSource } from '../lib/matchCanvasPipeline';
import { sourceLabelFor } from '../lib/matchCanvasPipeline';
import type { MatchResult } from '../types';
import { CopilotSummaryCard } from './CopilotSummaryCard';

export interface AgentThreadHandle {
  sendPrompt: (text: string) => void;
}

interface AgentThreadColumnProps {
  /** Pipeline id acts as the Smart Match scope id (§20.D persistence). */
  pipelineId: string;
  /** Initial prompt seeded from the intake page. Sent exactly once on mount. */
  initialPrompt?: string;
  /** Intake source surfaced in the header. */
  source: IntakeSource;
  /** When present, scoped tool calls reference this candidate id. */
  focusedCandidateId?: string | null;
  /** Expose imperative send handle (used by keyboard shortcuts + rail asks). */
  handleRef?: React.MutableRefObject<AgentThreadHandle | null>;
  /**
   * Phase 8 · Addendum F §33 — pick between the legacy T-06 tool-aware
   * `runPrompt` (Smart Match canvas, default) and the C-41 streaming
   * runner (`agentRunnerService.streamReply`) which transparently fans
   * out to every configured sourcing connector via `detectSourcingIntent`
   * and renders results through `AgentSourcingResults`.
   *
   * The Sourcing canvas (`/sourcing/:id`) sets this to `'streaming'` so
   * a prompt like "find senior React devs in Bangalore" dispatches
   * `source.internal.candidates` (+ any active external connector) in
   * parallel and renders the candidate cards inline — exactly the
   * Wrangle reference flow in §33.
   *
   * Smart Match keeps `'legacy'` until we migrate T-06's pipeline onto
   * the runner in a follow-up PR, so this is a non-breaking addition.
   */
  runnerVariant?: 'legacy' | 'streaming';
  /**
   * Phase 8 · Wrangle parity (§33, image 1 annotation 3) — matches
   * currently surfaced in the middle column. When non-empty the
   * `<CopilotSummaryCard>` renders above the conversation showing
   * score bins / avg / top. Undefined → no card (e.g. pre-result
   * state or a thread that never produced matches).
   */
  matches?: MatchResult[];
  /**
   * Wrangle parity (§33, image 2). Optional slot rendered at the top
   * of the column, *above* the Source header and the Copilot card.
   * The Sourcing canvas passes `<SourcingHistoryColumn variant="embedded">`
   * here so History, Copilot, chat, and the composer all stack in one
   * left column instead of History being a separate sibling column.
   */
  leadingSlot?: React.ReactNode;
  /**
   * Callback fired when sourcing tool results arrive. Used by the canvas
   * to surface external sourcing results as match cards in the grid.
   */
  onSourcingResults?: (results: import('@modules/agent/hooks/useAgentThread').AgentToolResult[]) => void;
}

export function AgentThreadColumn({
  pipelineId,
  initialPrompt,
  source,
  focusedCandidateId = null,
  handleRef,
  runnerVariant = 'legacy',
  matches,
  leadingSlot,
  onSourcingResults,
}: AgentThreadColumnProps) {
  const { user } = useAuth();
  const orgId = user?.orgId ?? '';
  const historyRef = useRef<LlamaChatMessage[]>([]);
  const seededRef = useRef(false);

  const runPrompt = useCallback(
    async (userText: string) => {
      const startedAt = performance.now();
      const contextLine = focusedCandidateId
        ? `\nWhen [Candidate context: ${focusedCandidateId}] is present, tools that filter by candidate id should use it.`
        : '';
      const systemPrompt = `${AGENT_SYSTEM_PROMPT}
When the thread contains [Agent context] under a previous reply, use it for follow-up questions instead of calling tools again.${contextLine}

${TOOL_DEFINITIONS}`;
      const contextPrefix = focusedCandidateId
        ? `[Candidate context: ${focusedCandidateId}] `
        : '';
      const nextHistory: LlamaChatMessage[] = [
        ...historyRef.current,
        { role: 'user', content: `${contextPrefix}${userText}` },
      ];
      const llamaMeta = orgId
        ? { orgId, feature: 'smart_match_canvas', orgUserId: user?.orgUserId ?? null }
        : null;

      const steps: AgentReasoningStep[] = [];
      const first = await invokeLlamaChat(
        [{ role: 'system', content: systemPrompt }, ...nextHistory],
        undefined,
        llamaMeta,
        { tools: OPENAI_TOOL_DEFINITIONS },
      );
      steps.push({ name: 'llm_plan', durationMs: performance.now() - startedAt });

      const rawResponse = first.content ?? '';
      let toolCall: ToolCall | null = null;
      if (first.toolCalls && first.toolCalls.length > 0) {
        toolCall = parseOpenAIToolCalls(first.toolCalls)[0] ?? null;
      } else {
        toolCall = parseToolCall(rawResponse);
      }

      if (toolCall && orgId) {
        const toolStart = performance.now();
        const toolResult = await executeTool(orgId, toolCall);
        steps.push({
          name: `tool:${toolCall.tool}`,
          durationMs: performance.now() - toolStart,
        });
        const summary = toolResultSummary(toolResult);
        const narrStart = performance.now();
        const { content: narration } = await invokeLlamaChat(
          [
            { role: 'system', content: systemPrompt },
            ...nextHistory,
            {
              role: 'assistant',
              content:
                rawResponse.trim() ||
                `[Called tool ${toolCall.tool} with ${JSON.stringify(toolCall.args)}]`,
            },
            {
              role: 'user',
              content: `Tool result: ${summary}. Summarise this for the recruiter in 1-2 sentences.`,
            },
          ],
          undefined,
          llamaMeta,
          null,
        );
        steps.push({ name: 'llm_summarise', durationMs: performance.now() - narrStart });

        historyRef.current = [
          ...nextHistory,
          { role: 'assistant', content: augmentAssistantHistoryForLlm(narration, toolResult) },
        ];
        const thoughtMs = performance.now() - startedAt;
        return {
          content: narration,
          reasoning: { thoughtMs, steps },
          chips: [
            { id: 'fetch-more', label: 'Fetch more' },
            { id: 'update-search', label: 'Update search' },
            { id: 'update-evals', label: 'Update evals' },
            { id: 'filter', label: 'Filter candidates' },
          ],
        };
      }

      historyRef.current = [...nextHistory, { role: 'assistant', content: rawResponse }];
      const thoughtMs = performance.now() - startedAt;
      return {
        content: rawResponse,
        reasoning: { thoughtMs, steps },
      };
    },
    [focusedCandidateId, orgId, user?.orgUserId],
  );

  // Expose the imperative send handle — Smart Match keyboard shortcuts and
  // CandidateContextRail's "Ask about this candidate…" input still use it.
  // It delegates by dispatching a synthetic prompt into the shared thread's
  // localStorage store, using the same scopeId the component renders.
  useEffect(() => {
    if (!handleRef) return;
    handleRef.current = {
      sendPrompt: (text: string) => {
        // Fire-and-forget: push a new turn into the thread by writing to the
        // shared hook's localStorage and triggering a storage event so the
        // thread re-reads. The thread itself owns the async run.
        dispatchExternalPrompt(pipelineId, text);
      },
    };
    return () => {
      if (handleRef.current) handleRef.current = null;
    };
  }, [handleRef, pipelineId]);

  // Seed the initial prompt exactly once.
  const seededPayload = initialPrompt && !seededRef.current ? initialPrompt : null;
  useEffect(() => {
    if (!seededPayload) return;
    seededRef.current = true;
    dispatchExternalPrompt(pipelineId, seededPayload);
  }, [pipelineId, seededPayload]);

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {/*
        Wrangle parity (§33, image 2). `leadingSlot` renders *above* the
        source header — the Sourcing canvas uses this to stack its
        persistent History panel atop the chat, matching the Wrangle
        reference where History, Copilot, chat, and composer all share
        one left column. Smart Match leaves this undefined.
      */}
      {leadingSlot ? <div className="shrink-0">{leadingSlot}</div> : null}
      {source === 'external' ? (
        <div className="shrink-0 border-b px-3 py-2.5" style={{ borderColor: 'var(--vscode-border-primary)' }}>
          <SourcingConnectorStatus variant="compact" />
        </div>
      ) : null}
      <div
        className="shrink-0 border-b px-4 py-2"
        style={{ borderColor: 'var(--vscode-border-primary)' }}
      >
        <p
          className="text-[10px] font-semibold uppercase tracking-wider"
          style={{ color: 'var(--vscode-text-tertiary)' }}
        >
          Source · {sourceLabelFor(source)}
        </p>
      </div>
      {/*
        Wrangle parity (§33, image 1). The card is a sibling of the chat
        thread — not inside it — so the thread's internal scroll anchors
        don't reset on matches change. `shrink-0` keeps the card visible
        while the thread fills the remaining height.
      */}
      {matches && matches.length > 0 ? (
        <div className="shrink-0 px-4 pt-3">
          <CopilotSummaryCard matches={matches} />
        </div>
      ) : null}
      <AgentThread
        scope="smart-match"
        scopeId={pipelineId}
        {...(runnerVariant === 'legacy' ? { runPrompt } : {})}
        showModelHeader
        onSourcingResults={onSourcingResults}
      />
    </div>
  );
}

// ── External prompt dispatch ───────────────────────────────────────────────
//
// The shared `<AgentThread>` owns its own send loop. To let external callers
// (keyboard shortcuts, candidate rail) inject a prompt, we emit a DOM-level
// CustomEvent that `useAgentThread` listens for on mount.

const EXTERNAL_PROMPT_EVENT = 'ggj:agent-thread:external-prompt';

function dispatchExternalPrompt(scopeId: string, text: string) {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(
    new CustomEvent(EXTERNAL_PROMPT_EVENT, { detail: { scopeId, text } }),
  );
}
